import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fetchAppointmentConfirmationDraft, setAdminAppointmentConfirmation } from "@/lib/api/admin";
import { fetchMessagesSettings } from "@/lib/api/settings";
import { ensurePatientThread } from "@/lib/api/threads";
import { sendConversationMessage } from "@/lib/realtime/chat-realtime-service";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { NIL_PATIENT_ID } from "./calendar-utils";

// Loose match key: lowercase alphanumerics only ("Toa Payoh" → "toapayoh") so
// spacing/case/punctuation differences between a clinic name and a WhatsApp
// number's label don't get in the way.
function normalizeForMatch(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

// botId of the number whose label appears in `haystack`, longest label first so
// a specific "Toa Payoh" beats an incidental short-code collision. "" if none.
function pickLongestLabelMatch(haystack, numbers) {
  const hay = normalizeForMatch(haystack);
  if (!hay) return "";
  let botId = "";
  let bestLen = 0;
  for (const n of numbers) {
    const needle = normalizeForMatch(n?.label);
    if (needle.length < 2) continue; // 1-char labels are too collision-prone
    if (needle.length > bestLen && hay.includes(needle)) {
      botId = n.botId;
      bestLen = needle.length;
    }
  }
  return botId;
}

// Fuzzy-match the appointment's clinic (surfaced in the confirmation draft) to a
// configured send-from number. The template renders the clinic as "… @<Location>",
// so that segment is matched first (high precision); the whole draft — clinic name
// and address both name the location — is the fallback. "" when nothing matches so
// the caller keeps the default number.
function matchSendFromToDraftClinic(draftText, numbers) {
  if (!Array.isArray(numbers) || numbers.length === 0) return "";
  const atSegment = String(draftText || "").match(/@([^\n*]+)/)?.[1] || "";
  return pickLongestLabelMatch(atSegment, numbers) || pickLongestLabelMatch(draftText, numbers);
}

/**
 * Staff-facing "review then send" dialog for an appointment confirmation.
 *
 * Fetches the tenant's per-type confirmation draft (rendered server-side from
 * the shared templates), shows it in an editable textarea, and on confirm sends
 * it to the patient over WhatsApp (find-or-create thread → send) then marks the
 * appointment's confirmation state "pending" so the card shows "Awaiting
 * confirmation". Patient-less bookings (no linked user) can't be messaged, so
 * Send is disabled — the draft is still shown for copy/paste.
 *
 * Standalone and self-contained: rendered per-call-site (the calendar hosts one
 * instance via the mutations context; the chat booking panel renders its own).
 */
export function AppointmentConfirmationDialog({
  open,
  onOpenChange,
  appointmentId,
  patientUserId,
  patientName,
  phone,
  onSent,
}) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  // Configured WhatsApp business numbers ([{botId,label}]) + the picked send-from.
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState("");
  // Bumps on every open so an in-flight draft fetch for a stale target is ignored.
  const fetchTokenRef = useRef(0);
  // Send-from auto-select guards: stop overriding once staff pick manually, and
  // run the clinic match at most once per open (so editing the draft can't move it).
  const userPickedRef = useRef(false);
  const autoSelectDoneRef = useRef(false);

  const trimmedPatientId = String(patientUserId || "").trim();
  const hasRealPatient = Boolean(trimmedPatientId) && trimmedPatientId !== NIL_PATIENT_ID;
  const aid = String(appointmentId || "").trim();
  const recipientName = String(patientName || "").trim();
  const recipientPhone = String(phone || "").trim();
  // A real patient messages their linked user; a new-patient booking (NIL id) can
  // still be messaged when it carries a phone — the backend find-or-creates a
  // patient by that number.
  const canSend = hasRealPatient || Boolean(recipientPhone);

  useEffect(() => {
    if (!open || !aid) return;
    const token = ++fetchTokenRef.current;
    setLoading(true);
    setError("");
    setText("");
    fetchAppointmentConfirmationDraft({
      userId: trimmedPatientId || NIL_PATIENT_ID,
      appointmentId: aid,
    })
      .then((res) => {
        if (fetchTokenRef.current !== token) return;
        setText(String(res?.text || ""));
      })
      .catch((err) => {
        if (fetchTokenRef.current !== token) return;
        setError(err?.message || "Could not generate the confirmation message.");
      })
      .finally(() => {
        if (fetchTokenRef.current !== token) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aid, trimmedPatientId]);

  // Load the configured WhatsApp business numbers so staff can pick which to send from.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchMessagesSettings()
      .then((res) => {
        if (!cancelled) setWhatsappNumbers(Array.isArray(res?.whatsappNumbers) ? res.whatsappNumbers : []);
      })
      .catch(() => {
        if (!cancelled) setWhatsappNumbers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset the auto-select guards on each open so a freshly targeted appointment
  // re-matches its clinic (and a prior manual pick doesn't carry over).
  useEffect(() => {
    if (!open) return;
    userPickedRef.current = false;
    autoSelectDoneRef.current = false;
  }, [open]);

  // Keep the send-from number valid: default to the first configured one as the
  // list loads (and whenever the current pick falls out of the list).
  useEffect(() => {
    if (!whatsappNumbers.length) return;
    setSelectedBotId((prev) =>
      prev && whatsappNumbers.some((n) => n.botId === prev) ? prev : whatsappNumbers[0].botId,
    );
  }, [whatsappNumbers]);

  // Once the draft has loaded, fuzzy-match its clinic to a number's label and adopt
  // it — one-shot per open, and never over a manual choice. Falls back to the
  // default (kept by the effect above) when nothing matches.
  useEffect(() => {
    if (!whatsappNumbers.length || loading) return;
    if (userPickedRef.current || autoSelectDoneRef.current) return;
    autoSelectDoneRef.current = true;
    const matched = matchSendFromToDraftClinic(text, whatsappNumbers);
    if (matched) setSelectedBotId(matched);
  }, [whatsappNumbers, loading, text]);

  const handleSend = async () => {
    const body = String(text || "").trim();
    if (!body || !canSend || sending) return;
    setSending(true);
    try {
      const { threadId } = await ensurePatientThread({
        // New patients have no linked user — message by phone instead.
        patientUserId: hasRealPatient ? trimmedPatientId : undefined,
        displayName: recipientName || undefined,
        phone: recipientPhone || undefined,
        // Which business number to send from (and bind); the backend finds-or-creates
        // the per-number thread and binds the gateway + concierge assistant.
        whatsappBotId: selectedBotId || undefined,
      });
      if (!threadId) {
        throw new Error("Could not open a conversation for this patient.");
      }
      sendConversationMessage(threadId, body, {
        metadata: { kind: "appointmentConfirmationRequest", appointmentId: aid },
      });
      // Mark "pending" so the card flips to "Awaiting confirmation". Non-fatal if
      // this fails — the message already went out.
      try {
        await setAdminAppointmentConfirmation({
          // Key on the zero-id sentinel for a new patient so the pending mark lands
          // on the appointment by id (same as the "Mark confirmed" action).
          userId: trimmedPatientId || NIL_PATIENT_ID,
          appointmentId: aid,
          status: "pending",
        });
        publishCalendarRefresh({ source: "appointment-confirmation", appointmentId: aid });
      } catch (markErr) {
        console.warn("appointment confirmation: mark pending failed", markErr);
      }
      toast.success("Confirmation sent to patient", {
        description: "We'll mark the appointment confirmed when they reply.",
      });
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not send the confirmation", {
        description: err?.message || undefined,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onOpenChange(false))}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send appointment confirmation</DialogTitle>
          <DialogDescription>
            {canSend
              ? "Review the message below, edit if needed, then send it to the patient over WhatsApp."
              : "This appointment has no linked patient to message. You can copy the text below or mark it confirmed manually."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {canSend && (recipientName || whatsappNumbers.length > 1) ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <div className="min-w-0">
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">To</div>
                <div className="truncate text-sm font-medium text-foreground">
                  {recipientName || "Patient"}
                </div>
                {recipientPhone ? (
                  <div className="font-mono text-[11px] text-muted-foreground">{recipientPhone}</div>
                ) : null}
              </div>
              {whatsappNumbers.length > 1 ? (
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <label
                    htmlFor="wa-confirm-send-from"
                    className="text-[11px] font-medium text-muted-foreground"
                  >
                    Send from
                  </label>
                  <select
                    id="wa-confirm-send-from"
                    value={selectedBotId}
                    onChange={(e) => {
                      userPickedRef.current = true;
                      setSelectedBotId(e.target.value);
                    }}
                    disabled={sending}
                    className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {whatsappNumbers.map((n) => (
                      <option key={n.botId} value={n.botId}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : null}
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              Generating confirmation…
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              className="font-mono text-sm leading-relaxed"
              placeholder="Confirmation message…"
            />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={!canSend || loading || sending || !String(text || "").trim()}
          >
            {sending ? "Sending…" : "Send to patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AppointmentConfirmationDialog;
